import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { LoaderCircle } from 'lucide-react'
import { logLevelVariant } from '@/lib/status-variants'
import { Query } from '../../../../api'

interface Props {
  jobId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const AiProcessingLogModal: React.FC<Props> = ({ jobId, open, onOpenChange }) => {
  const { t } = useTranslation()

  const { data, loading } = useQuery(Query.AI_PROCESSING_JOB, {
    variables: { id: jobId },
    skip: !jobId || !open,
  })

  const job = data?.aiProcessingJob
  const logs = job?.logs || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('ai_processing_logs')}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <LoaderCircle className="h-5 w-5 animate-spin mr-2" />
            {t('loading')}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            {t('ai_no_logs')}
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 rounded-md border divide-y">
            {logs.map((log: any) => (
              <div
                key={log.id}
                className="flex items-start gap-2 px-3 py-1.5 text-sm"
              >
                <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                {log.level && log.level !== 'INFO' && (
                  <Badge
                    variant={logLevelVariant(log.level)}
                    className="font-medium whitespace-nowrap text-[0.65rem] py-0 px-1"
                  >
                    {log.level}
                  </Badge>
                )}
                <span className="break-all">{log.message}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AiProcessingLogModal
